// Browser-safe checkout contracts. Payment and shipping integrations plug into the storefront
// through these adapter shapes; no gateway or carrier SDK may be imported by the UI.

export const CHECKOUT_STEPS = Object.freeze(['cart', 'address', 'review', 'confirmation']);

// Error codes every commerce client maps transport/API failures to, so the UI can tell a
// retryable outage from an expired cart or a rejected change (price/stock revalidated by the API).
export const COMMERCE_ERROR = Object.freeze({
  UNAVAILABLE: 'unavailable',
  NOT_FOUND: 'not_found',
  INVALID: 'invalid',
});

export class CommerceError extends Error {
  constructor(code, message, { status, cause } = {}) {
    super(message, cause ? { cause } : undefined);
    this.name = 'CommerceError';
    this.code = code;
    this.status = status;
  }
}

export const isCommerceError = (error, code) =>
  error instanceof CommerceError && (code === undefined || error.code === code);

const ADAPTER_METHODS = {
  // listOptions(cart) -> [{ id, label, amount }]; select(cart, optionId) -> cart
  shipping: ['listOptions', 'select'],
  // prepare(cart) -> void, run right before the order is placed
  payment: ['prepare'],
};

/**
 * Validates a payment/shipping adapter and returns it frozen.
 * @param {'payment'|'shipping'} kind
 * @param {{ id: string, label: string, capabilities?: string[] }} adapter
 */
export function defineAdapter(kind, adapter) {
  const methods = ADAPTER_METHODS[kind];
  if (!methods) {
    throw new TypeError(`unknown adapter kind: ${kind}`);
  }
  if (!adapter || typeof adapter.id !== 'string' || !adapter.id) {
    throw new TypeError(`${kind} adapter needs a non-empty string id`);
  }
  if (typeof adapter.label !== 'string' || !adapter.label) {
    throw new TypeError(`${kind} adapter ${adapter.id} needs a label`);
  }
  for (const method of methods) {
    if (typeof adapter[method] !== 'function') {
      throw new TypeError(`${kind} adapter ${adapter.id} must implement ${method}()`);
    }
  }
  return Object.freeze({ capabilities: [], ...adapter, kind });
}

export const ADDRESS_FIELDS = Object.freeze([
  'email',
  'first_name',
  'last_name',
  'phone',
  'postal_code',
  'address_1',
  'address_2',
  'city',
  'province',
]);

const REQUIRED_ADDRESS_FIELDS = ['email', 'first_name', 'last_name', 'postal_code', 'address_1', 'city', 'province'];

/** Returns `{ field: message }` for the invalid fields of a Brazilian shipping address. */
export function validateAddress(address = {}) {
  const errors = {};
  for (const field of REQUIRED_ADDRESS_FIELDS) {
    if (!String(address[field] ?? '').trim()) {
      errors[field] = 'Campo obrigatório';
    }
  }
  if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email.trim())) {
    errors.email = 'E-mail inválido';
  }
  if (!errors.postal_code && !/^\d{5}-?\d{3}$/.test(address.postal_code.trim())) {
    errors.postal_code = 'CEP inválido';
  }
  if (!errors.province && !/^[A-Za-z]{2}$/.test(address.province.trim())) {
    errors.province = 'Use a sigla do estado (ex.: PR)';
  }
  return errors;
}

/** Amounts come from the API in major units (Medusa v2), e.g. 69.9 BRL. */
export function formatMoney(amount, currencyCode = 'brl', locale = 'pt-BR') {
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return '';
  }
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode.toUpperCase() }).format(amount);
}
